// One line of guidance for an analytics_query failure the API reports only as
// a position in the request: a JSON key the Query message does not have, or an
// enum value that does not exist. Other failures already say what to do
// (narrow the window, add a LIKE term, lower the limit) and get nothing here.
// The live schema the chat loaded says what is valid: the keys its runnable
// examples use at the same place in a query, and each entity's metrics and
// fields.

import { extractMessage } from "./harnessMemoryCapture.js";

const UNKNOWN_KEY = /unknown field "([^"]+)"/;
const BAD_ENUM = /invalid value for enum field (\w+): "([^"]+)"/;
const MAX_NAMES = 12;

// The path of the first object in `value` that has `key` as its own key:
// "" for the top level, "filter" for a key of `filter`, "output.metrics[]"
// for a key of an element of `output.metrics`. null when no object has it.
function pathOf(value, key, path = "") {
    if (Array.isArray(value)) {
        for (const item of value) {
            const found = pathOf(item, key, `${path}[]`);
            if (found !== null) return found;
        }
        return null;
    }
    if (!value || typeof value !== "object") return null;
    if (Object.hasOwn(value, key)) return path;
    for (const [k, child] of Object.entries(value)) {
        const found = pathOf(child, key, path ? `${path}.${k}` : k);
        if (found !== null) return found;
    }
    return null;
}

// The keys of the objects at `path` in `value`, by the same path scheme.
function keysAt(value, path, into = new Set(), at = "") {
    if (Array.isArray(value)) {
        for (const item of value) keysAt(item, path, into, `${at}[]`);
        return into;
    }
    if (!value || typeof value !== "object") return into;
    if (at === path) {
        for (const key of Object.keys(value)) into.add(key);
        return into;
    }
    for (const [k, child] of Object.entries(value)) keysAt(child, path, into, at ? `${at}.${k}` : k);
    return into;
}

// Every value of `key` anywhere in `value`.
function valuesOf(value, key, into = []) {
    if (Array.isArray(value)) {
        for (const item of value) valuesOf(item, key, into);
    } else if (value && typeof value === "object") {
        for (const [k, child] of Object.entries(value)) {
            if (k === key && typeof child === "string") into.push(child);
            else valuesOf(child, key, into);
        }
    }
    return into;
}

function exampleQueries(schema) {
    const examples = Array.isArray(schema?.examples) ? schema.examples : [];
    return examples.map((e) => e?.query || e?.spec).filter((q) => q && typeof q === "object");
}

function distance(a, b) {
    const s = a.toLowerCase();
    const t = b.toLowerCase();
    let prev = Array.from({ length: t.length + 1 }, (_, j) => j);
    for (let i = 1; i <= s.length; i++) {
        const row = [i];
        for (let j = 1; j <= t.length; j++) {
            row[j] = Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + (s[i - 1] === t[j - 1] ? 0 : 1));
        }
        prev = row;
    }
    return prev[t.length];
}

// A name that starts the other (`or` and `orExpr`) is the likeliest meant,
// then the fewest edits.
function nearness(name, candidate) {
    const a = name.toLowerCase();
    const b = candidate.toLowerCase();
    return [a.startsWith(b) || b.startsWith(a) ? 0 : 1, distance(a, b)];
}

function closest(name, candidates) {
    return [...new Set(candidates)]
        .map((c) => [c, nearness(name, c)])
        .sort(([x, nx], [y, ny]) => nx[0] - ny[0] || nx[1] - ny[1] || x.localeCompare(y))
        .slice(0, MAX_NAMES)
        .map(([c]) => c);
}

function isNear(name, candidate) {
    const [prefix, edits] = nearness(name, candidate);
    return prefix === 0 || edits <= 2;
}

// Every path at which an object in `value` has `key` as its own key.
function pathsOf(value, key, path = "", into = new Set()) {
    if (Array.isArray(value)) {
        for (const item of value) pathsOf(item, key, `${path}[]`, into);
    } else if (value && typeof value === "object") {
        if (Object.hasOwn(value, key)) into.add(path);
        for (const [k, child] of Object.entries(value)) pathsOf(child, key, path ? `${path}.${k}` : k, into);
    }
    return into;
}

const quoted = (names) => names.map((n) => `\`${n}\``).join(", ");

function unknownKeyGuidance(key, spec, schema) {
    const at = pathOf(spec, key);
    if (at === null) return null;
    const examples = exampleQueries(schema);
    const keys = new Set();
    const usedAt = new Set();
    for (const query of examples) {
        keysAt(query, at, keys);
        pathsOf(query, key, "", usedAt);
    }
    keys.delete(key);
    usedAt.delete(at);
    if (!keys.size && !usedAt.size) return null;
    const where = at ? `in \`${at}\`` : "at the top level of the query";
    const parts = [`\`${key}\` is not a field ${where}.`];
    const names = closest(key, keys);
    if (names.length && isNear(key, names[0])) parts.push(`Did you mean \`${names[0]}\`?`);
    if (names.length) parts.push(`The schema's examples use these keys there: ${quoted(names)}.`);
    if (usedAt.size) {
        const places = [...usedAt].slice(0, 2).map((p) => (p ? `\`${p}\`` : "the top level"));
        parts.push(`They use \`${key}\` in ${places.join(" and ")}.`);
    }
    return parts.join(" ");
}

function entityNamesIn(spec) {
    return [...new Set(valuesOf(spec, "entity").filter((v) => v.startsWith("ENTITY_TYPE_")))];
}

function descriptorName(d, key) {
    return typeof d === "string" ? d : d?.[key] || d?.name || null;
}

function badEnumGuidance(field, value, spec, schema) {
    const entities = entityNamesIn(spec);
    const descriptors = (Array.isArray(schema?.entities) ? schema.entities : [])
        .filter((e) => entities.includes(e?.entity));
    const served = entities.length ? entities.map((e) => `\`${e}\``).join(", ") : "the queried entity";
    let names = [];
    let lead = "";
    if (value.startsWith("METRIC_TYPE_")) {
        names = descriptors.flatMap((e) => (e.metrics || []).map((m) => descriptorName(m, "metric")));
        lead = `The metrics ${served} serves, closest first:`;
    } else if (value.startsWith("DIMENSION_FIELD_")) {
        names = descriptors.flatMap((e) => (e.fields || []).map((f) => descriptorName(f, "field")));
        lead = `The fields ${served} serves, closest first:`;
    }
    if (!names.filter(Boolean).length) {
        names = exampleQueries(schema).flatMap((query) => valuesOf(query, field));
        lead = `The schema's examples use these values for \`${field}\`:`;
    }
    names = closest(value, names.filter((n) => n && n !== value));
    if (!names.length) return null;
    return `\`${value}\` is not a valid \`${field}\`. ${lead} ${quoted(names)}.`;
}

export function queryErrorGuidance({ errorText, spec, schema }) {
    if (!schema || !spec || typeof spec !== "object") return null;
    const message = extractMessage(errorText).replace(/\\"/g, "\"");
    const badEnum = message.match(BAD_ENUM);
    if (badEnum) return badEnumGuidance(badEnum[1], badEnum[2], spec, schema);
    const unknownKey = message.match(UNKNOWN_KEY);
    if (unknownKey && message.includes("proto:")) return unknownKeyGuidance(unknownKey[1], spec, schema);
    return null;
}

// Wraps analytics_query so such a failure reaches the model with the line
// after the API's own message. Harness memory and graph hints, wrapped
// outside this, follow it.
export function withQueryGuidance(tool, { getSchema }) {
    if (!tool || typeof tool.execute !== "function") return tool;
    return {
        ...tool,
        async execute(toolCallId, args, ...rest) {
            try {
                return await tool.execute(toolCallId, args, ...rest);
            } catch (err) {
                let guidance = null;
                try {
                    const spec = typeof args?.spec === "string" ? JSON.parse(args.spec) : args?.spec;
                    guidance = queryErrorGuidance({ errorText: err?.message ?? String(err), spec, schema: getSchema?.() });
                } catch { guidance = null; }
                if (!guidance) throw err;
                const augmented = err instanceof Error ? err : new Error(String(err));
                try { augmented.message = `${augmented.message}\n\n${guidance}`; } catch { /* frozen message: surface the bare error */ }
                throw augmented;
            }
        },
    };
}
